// useTryRequest — orchestrates a single Try-it call.
// - Substitutes path params, appends query string, attaches Authorization header.
// - Tracks elapsed time, status, headers, body.
// - On every completed call (success OR HTTP error) appends to history store.

import { useCallback, useState } from 'react';
import { useApiKey } from '@/stores/apiKey.store';
import { useHistoryStore } from '@/stores/history.store';
import { substitutePath, buildQueryString } from '@/lib/openapi/form-builder';
import { buildApiUrl } from '@/lib/api-url';

export interface TryResponse {
    status: number;
    statusText: string;
    headers: Record<string, string>;
    body: unknown;
    bodyRaw: string;
    elapsedMs: number;
    url: string;
}

export interface SubmitArgs {
    operationId?: string;
    method: string;
    path: string;
    pathParams?: Record<string, string>;
    queryParams?: Record<string, string>;
    headers?: Record<string, string>;
    body?: string; // raw JSON string
}

interface State {
    submit: (args: SubmitArgs) => Promise<TryResponse | null>;
    busy: boolean;
    response: TryResponse | null;
    error: string | null;
    reset: () => void;
}

export function useTryRequest(): State {
    const { key } = useApiKey();
    const addEntry = useHistoryStore((s) => s.addEntry);
    const [busy, setBusy] = useState(false);
    const [response, setResponse] = useState<TryResponse | null>(null);
    const [error, setError] = useState<string | null>(null);

    const submit = useCallback(async (args: SubmitArgs): Promise<TryResponse | null> => {
        setBusy(true);
        setError(null);
        setResponse(null);

        const method = args.method.toUpperCase();
        const finalPath = substitutePath(args.path, args.pathParams || {});
        const qs = buildQueryString(args.queryParams || {});
        // OpenAPI paths are bare (e.g. `/workspaces`); routes mounted at /api/v1/*.
        const url = `${buildApiUrl(finalPath)}${qs}`;

        const headers: Record<string, string> = {};
        // User-supplied headers go first; Authorization + Content-Type below take precedence
        // so users can't accidentally override the auth scheme by typing into a header field.
        if (args.headers) {
            for (const [k, v] of Object.entries(args.headers)) {
                if (v) headers[k] = v;
            }
        }
        if (key) headers['Authorization'] = `Bearer ${key}`;
        const hasBody = args.body && args.body.trim().length > 0 && method !== 'GET' && method !== 'DELETE';
        if (hasBody) headers['Content-Type'] = 'application/json';

        const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
        try {
            const init: RequestInit = { method, headers };
            if (hasBody) init.body = args.body;
            const res = await fetch(url, init);
            const elapsedMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);

            const respHeaders: Record<string, string> = {};
            res.headers.forEach((v, k) => { respHeaders[k] = v; });

            const bodyRaw = await res.text();
            let bodyParsed: unknown = bodyRaw;
            const ct = res.headers.get('content-type') || '';
            if (ct.includes('json') && bodyRaw) {
                try { bodyParsed = JSON.parse(bodyRaw); }
                catch { /* keep raw */ }
            }

            const out: TryResponse = {
                status: res.status,
                statusText: res.statusText,
                headers: respHeaders,
                body: bodyParsed,
                bodyRaw,
                elapsedMs,
                url,
            };
            setResponse(out);
            addEntry({
                operationId: args.operationId,
                method,
                path: args.path,
                url,
                status: res.status,
                elapsedMs,
                pathParams: args.pathParams,
                queryParams: args.queryParams,
                body: args.body,
            });
            return out;
        } catch (e: unknown) {
            const elapsedMs = Math.round((typeof performance !== 'undefined' ? performance.now() : Date.now()) - t0);
            const msg = e instanceof Error ? e.message : 'Request gagal';
            setError(msg);
            addEntry({
                operationId: args.operationId,
                method,
                path: args.path,
                url,
                status: 0,
                elapsedMs,
                pathParams: args.pathParams,
                queryParams: args.queryParams,
                body: args.body,
            });
            return null;
        } finally {
            setBusy(false);
        }
    }, [key, addEntry]);

    const reset = useCallback(() => {
        setResponse(null);
        setError(null);
    }, []);

    return { submit, busy, response, error, reset };
}
